<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isLoading">

    <app-page-detail-hero
        [eyebrow]="siteHeroEyebrow()"
        [title]="siteHeroTitle()"
        [subtitle]="siteHeroSubtitle()"
        icon="fa-solid fa-location-dot">
        <div tags class="hero-status-tags">
            <p-tag styleClass="status-level-tag" [value]="'Site workflow: ' + (selectedStop?.workflowState || 'PENDING')" [severity]="getStatusSeverity(selectedStop?.workflowState)"></p-tag>
            <p-tag styleClass="status-level-tag" [value]="'Evidence gate: ' + (selectedStop?.evidenceStatus || 'PENDING')" severity="info"></p-tag>
            <p-tag styleClass="status-level-tag" [value]="'Delivery route: ' + (operationStatus?.deliveryStatus || shipmentDelivery?.workflowState || 'PLANNED')" severity="secondary"></p-tag>
            <p-tag *ngIf="selectedStop?.stopPurpose === 'REJECTION_RETURN'" styleClass="status-level-tag"
                [value]="selectedStop?.returnDestinationConfirmedAt ? 'Return destination confirmed' : 'Awaiting office confirmation'"
                [severity]="selectedStop?.returnDestinationConfirmedAt ? 'success' : 'warn'"></p-tag>
            <p-tag *ngIf="selectedStop?.stopPurpose === 'REVISIT'" styleClass="status-level-tag"
                value="Required revisit" severity="warn"></p-tag>
            <p-tag *ngIf="selectedStop?.stopPurpose !== 'REJECTION_RETURN'" styleClass="status-level-tag"
                [value]="(selectedStop?.waybillNumber || (selectedStop?.stopType === 'DROPOFF' ? shipmentDelivery?.waybillNumber : '')) ? ('Waybill: ' + (selectedStop?.waybillNumber || shipmentDelivery?.waybillNumber)) : 'No waybill issued'"
                [severity]="(selectedStop?.waybillNumber || (selectedStop?.stopType === 'DROPOFF' ? shipmentDelivery?.waybillNumber : '')) ? 'success' : 'secondary'"></p-tag>
        </div>
        <div actions class="hero-action-group">
            <button pButton type="button" label="Back to delivery" icon="pi pi-arrow-left"
                severity="secondary" [outlined]="true" class="hero-back-button"
                (click)="goBackToDelivery()"></button>
        </div>
    </app-page-detail-hero>

    <div class="cancellation-alert-banner" *ngIf="isCancelled()">
        <i class="fa-solid fa-ban"></i>
        <div class="cancellation-alert-copy">
            <strong>This shipment or delivery has been cancelled.</strong>
            <span>Site operations, workflow transitions, and payload modifications are locked.</span>
        </div>
    </div>

    <div class="cancellation-alert-banner" *ngIf="selectedStop?.stopPurpose === 'REJECTION_RETURN' && !selectedStop?.returnDestinationConfirmedAt">
        <i class="fa-solid fa-clock"></i>
        <div class="cancellation-alert-copy">
            <strong>Return destination awaiting office confirmation.</strong>
            <span>This stop cannot be started until the destination is confirmed from the delivery page.</span>
        </div>
    </div>

    <app-page-metric-strip [metrics]="siteMetrics()"></app-page-metric-strip>

    <section class="operation-guide" aria-label="Site operation steps">
        <div class="guide-intro">
            <span>WORKFLOW</span>
            <strong>Complete this stop in three clear steps</strong>
        </div>
        <div class="guide-step">
            <span class="guide-number">1</span>
            <div>
                <strong>Confirm site status</strong>
                <small>Mark arrival before handling.</small>
            </div>
        </div>
        <i class="pi pi-arrow-right guide-arrow" aria-hidden="true"></i>
        <div class="guide-step">
            <span class="guide-number">2</span>
            <div>
                <strong>Record payload</strong>
                <small>{{ selectedStop?.stopType === 'PICKUP' ? 'Untouched pickup lines are accepted at their planned quantity when loading completes.' : 'Enter planned and actual quantities before completion.' }}</small>
            </div>
        </div>
        <i class="pi pi-arrow-right guide-arrow" aria-hidden="true"></i>
        <div class="guide-step">
            <span class="guide-number">3</span>
            <div>
                <strong>Add evidence</strong>
                <small>Upload proof, then complete the stop.</small>
            </div>
        </div>
    </section>

    <div class="site-layout">
        <div class="site-sidebar">
            <app-page-data-panel title="Site Control" subtitle="Stop-level workflow and evidence gate state.">
                <div class="task-callout">
                    <span class="task-callout-number">1</span>
                    <div>
                        <strong>Confirm what happened at this site</strong>
                        <small>{{ selectedStop?.stopType === 'PICKUP' ? 'Arrive and start loading. Completing the pickup marks untouched payload lines as loaded.' : 'Arrive, start handling, then record cargo outcomes before completing this stop.' }}</small>
                    </div>
                </div>
                <section class="site-summary">
                    <div>
                        <span>Stop Type</span>
                        <strong>{{ selectedStop?.stopPurpose === 'REJECTION_RETURN' ? 'RETURN HANDOFF' : (selectedStop?.stopType || 'STOP') }}</strong>
                    </div>
                    <div>
                        <span>Location</span>
                        <strong>{{ selectedStop?.locationName || ('Location ' + (selectedStop?.locationId || '-')) }}</strong>
                    </div>
                    <div>
                        <span>Planned Arrival</span>
                        <strong>{{ formatDate(selectedStop?.scheduledArrival) }}</strong>
                    </div>
                    <div>
                        <span>Actual Arrival</span>
                        <strong>{{ formatDate(selectedStop?.actualArrival) }}</strong>
                    </div>
                    <div>
                        <span>Planned Departure</span>
                        <strong>{{ formatDate(selectedStop?.scheduledDeparture) }}</strong>
                    </div>
                    <div>
                        <span>Actual Departure</span>
                        <strong>{{ formatDate(selectedStop?.actualDeparture) }}</strong>
                    </div>
                </section>

                @if (availableStopStateOptions().length > 0) {
                <div class="action-bar site-status-actions">
                    @for (state of availableStopStateOptions(); track state) {
                    <button pButton type="button" [label]="stopActionLabel(state)" [icon]="stopActionIcon(state)"
                        [outlined]="state === 'EXCEPTION' || state === 'SKIPPED'"
                        [severity]="state === 'EXCEPTION' ? 'danger' : state === 'SKIPPED' ? 'secondary' : 'success'"
                        class="site-action-button" (click)="transitionStop(state)"></button>
                    }
                    @if (selectedStop?.stopPurpose !== 'REJECTION_RETURN'
                        && (permissionService.hasPermission(app_permissions.shipments.deliveries.manage_stops)
                            || permissionService.hasPermission(app_permissions.shipments.deliveries.edit))) {
                    @if (selectedStop?.stopPurpose !== 'REVISIT'
                        && (selectedStop?.workflowState === 'PENDING' || selectedStop?.workflowState === 'ARRIVED')) {
                    <button pButton type="button" label="Skip for revisit" icon="fa-solid fa-forward-step"
                        severity="secondary" [outlined]="true" (click)="openSiteDecision('SKIP')"></button>
                    }
                    @if (selectedStop?.workflowState === 'WORKING' || selectedStop?.workflowState === 'EVIDENCE_PENDING') {
                    <button pButton type="button" label="Reject site" icon="fa-solid fa-ban"
                        severity="danger" [outlined]="true" (click)="openSiteDecision('REJECT')"></button>
                    }
                    @if (selectedStop?.workflowState === 'PENDING' || selectedStop?.workflowState === 'ARRIVED'
                        || selectedStop?.workflowState === 'WORKING' || selectedStop?.workflowState === 'EVIDENCE_PENDING') {
                    <button pButton type="button" label="Reschedule site" icon="fa-solid fa-calendar-days"
                        severity="warn" [outlined]="true" (click)="openSiteDecision('RESCHEDULE')"></button>
                    }
                    }
                </div>
                } @else {
                <div class="terminal-note">
                    <i class="fa-solid fa-circle-check"></i>
                    <span>{{ selectedStop?.workflowState || 'PENDING' }} has no standard status actions.</span>
                </div>
                }

                <div class="site-evidence-panel">
                    <div class="evidence-panel-header">
                        <div>
                            <strong>{{ evidenceSectionTitle() }}</strong>
                            <span>{{ stopEvidenceAttachments.length }} file(s)</span>
                        </div>
                        <button pButton type="button" [label]="evidenceActionLabel()" [icon]="evidenceActionIcon()"
                            *ngIf="!isCancelled()"
                            severity="info" (click)="openEvidenceUpload()"></button>
                    </div>
                    <span>Evidence upload is separate from status changes. Upload evidence here, then complete the stop when handling is done.</span>
                    <p-progressBar mode="indeterminate" *ngIf="isLoadingStopEvidence"></p-progressBar>
                    @if (stopEvidenceAttachments.length > 0) {
                    <div class="site-evidence-list">
                        @for (file of stopEvidenceAttachments; track file.attachmentId || file.fileUrl) {
                        <div class="site-evidence-row">
                            <app-attachment-preview [url]="file.fileUrl" [name]="file.fileName"></app-attachment-preview>
                            <div>
                                <a [href]="file.fileUrl" target="_blank" rel="noopener"><strong>{{ file.fileName }}</strong></a>
                                <span>{{ file.fileType || 'Evidence' }} | {{ formatBytes(file.fileSize) }} | {{ formatDate(file.creationDate) }}</span>
                            </div>
                        </div>
                        }
                    </div>
                    } @else if (!isLoadingStopEvidence) {
                    <div class="site-evidence-empty">
                        <i class="fa-solid fa-folder-open"></i>
                        <span>{{ evidenceEmptyMessage() }}</span>
                    </div>
                    }
                </div>

                <div class="override-note" *ngIf="canOverride()">Gate failures can still be retried with an override reason when an override is valid.</div>
            </app-page-data-panel>

            <app-page-data-panel title="Workflow Timeline" subtitle="Immutable workflow events recorded for this delivery.">
                @if (workflowEvents.length > 0) {
                <div class="event-list">
                    @for (event of workflowEvents; track event.eventId || $index) {
                    <div class="event-row" [class.current-site]="event.stopId === stopId">
                        <div>
                            <span>{{ event.level || 'EVENT' }}</span>
                            <strong>{{ event.title || ((event.previousState || '-') + ' -> ' + event.nextState) }}</strong>
                            <small>{{ event.description || 'Workflow transition' }}</small>
                        </div>
                        <div>
                            <p-tag [value]="event.gateResult || 'PASSED'" [severity]="event.gateResult === 'PASSED' ? 'success' : 'warning'"></p-tag>
                            <small>{{ formatDate(event.eventAt) }}</small>
                        </div>
                    </div>
                    }
                </div>
                } @else {
                <div class="inline-empty">No workflow events have been recorded for this delivery.</div>
                }
            </app-page-data-panel>
        </div>

        <div class="site-main">
            <app-page-data-panel title="Payload Operations" subtitle="Assign pickup/dropoff quantities to this stop and record actual handling.">
                <div class="task-callout payload-task-callout">
                    <span class="task-callout-number">2</span>
                    <div>
                        <strong>Record the payload handled here</strong>
                        <small>Add the required line, enter actual quantities, then save before changing its status.</small>
                    </div>
                </div>
                <div class="payload-warning" *ngIf="payloadAssignmentWarning() as warning">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span>{{ warning }}</span>
                </div>

                <div class="line-builder" *ngIf="!isCancelled() && selectedStop?.workflowState !== 'COMPLETE' && !hasRejectionReturn()">
                    <div class="line-builder-heading">
                        <div>
                            <span>NEW PAYLOAD LINE</span>
                            <strong>Choose what will be handled at this stop</strong>
                        </div>
                        <small>Fields marked by the workflow must be completed before saving.</small>
                    </div>
                    <div class="line-builder-fields">
                        <div class="field-block payload-field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <p-select inputId="newLinePayload" [(ngModel)]="newLineShipmentLineId"
                                        [options]="payloadSelectOptions()" optionLabel="label" optionValue="value"
                                        placeholder="Select payload" [filter]="true" filterBy="label"
                                        appendTo="body" styleClass="w-full"></p-select>
                                    <label for="newLinePayload">Payload item</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                        <div class="field-block">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-directions"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <p-select inputId="newLineAction" [ngModel]="newLineAction"
                                        (ngModelChange)="onNewLineActionChange($event)"
                                        [options]="actionOptionsForSelectedStop()" styleClass="w-full"></p-select>
                                    <label for="newLineAction">Action</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                        <div class="field-block counterpart-field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <p-select inputId="newLineCounterpart" [(ngModel)]="newLineCounterpartStopId"
                                        [options]="counterpartStopOptions()" optionLabel="label" optionValue="value"
                                        placeholder="No paired stop" appendTo="body" styleClass="w-full"></p-select>
                                    <label for="newLineCounterpart">{{ counterpartFieldLabel() }}</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                        <div class="field-block quantity-field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <p-inputNumber inputId="newLinePlannedQuantity" [(ngModel)]="newLinePlannedQuantity"
                                        [min]="0.001" [step]="0.001" [maxFractionDigits]="3"
                                        styleClass="w-full"></p-inputNumber>
                                    <label for="newLinePlannedQuantity">Planned quantity</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                    </div>
                    <div class="line-builder-actions">
                        <button pButton type="button" label="Add payload line" icon="pi pi-plus"
                            severity="secondary" [outlined]="true" (click)="addStopLine()"></button>
                        <button pButton type="button" label="Save payload operations" icon="pi pi-save"
                            [loading]="isSavingLines" (click)="saveStopLines()"></button>
                    </div>
                </div>

                @if ((selectedStop?.lineItems || []).length > 0) {
                @if (rejectedLineCount() > 0) {
                <div class="payload-status-strip"><i class="fa-solid fa-ban"></i>
                    <div><span>Rejected at this site</span><strong>{{ rejectedLineCount() }} line(s)</strong></div>
                </div>
                }
                <div class="stop-line-list">
                    @for (line of selectedStop.lineItems; track line.stopLineId || $index; let index = $index) {
                    <article class="stop-line-card">
                        <div class="line-title">
                            <div>
                                <span>{{ line.action || 'ACTION' }}</span>
                                <strong>{{ payloadLabelByLineId(line.shipmentLineId) }}</strong>
                            </div>
                            <p-tag [value]="line.workflowState || 'PLANNED'" [severity]="getStatusSeverity(line.workflowState)"></p-tag>
                        </div>

                        <div class="line-grid">
                            <div class="field-block">
                                <p-inputgroup>
                                    <p-inputgroup-addon><i class="pi pi-directions"></i></p-inputgroup-addon>
                                    <p-iftalabel>
                                        <p-select [inputId]="'lineAction' + index" [(ngModel)]="line.action"
                                            [disabled]="isPayloadPlanLocked(line)"
                                            [options]="actionOptionsForSelectedStop()" styleClass="w-full"></p-select>
                                        <label [for]="'lineAction' + index">Action</label>
                                    </p-iftalabel>
                                </p-inputgroup>
                            </div>
                            <div class="field-block">
                                <p-inputgroup>
                                    <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                                    <p-iftalabel>
                                        <p-inputNumber [inputId]="'linePlannedQuantity' + index"
                                            [disabled]="isPayloadPlanLocked(line)"
                                            [(ngModel)]="line.plannedQuantity" [min]="0" [step]="0.001"
                                            [maxFractionDigits]="3" styleClass="w-full"></p-inputNumber>
                                        <label [for]="'linePlannedQuantity' + index">Planned quantity</label>
                                    </p-iftalabel>
                                </p-inputgroup>
                            </div>
                            <div class="field-block">
                                <p-inputgroup>
                                    <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                                    <p-iftalabel>
                                        <p-inputNumber [inputId]="'lineActualQuantity' + index"
                                            [disabled]="true"
                                            [(ngModel)]="line.actualQuantity" [min]="0" [step]="0.001"
                                            [maxFractionDigits]="3" styleClass="w-full"></p-inputNumber>
                                        <label [for]="'lineActualQuantity' + index">Actual quantity</label>
                                    </p-iftalabel>
                                </p-inputgroup>
                            </div>
                            @if (line.exceptionReason) {
                            <div class="field-block exception-field"><small>Historical exception</small>
                                <div>{{ line.exceptionReason }}</div></div>
                            }
                        </div>

                        <div class="payload-status-strip">
                            <i [class]="payloadStateIcon(currentPayloadState(line))"></i>
                            <div>
                                <span>Current payload status</span>
                                <strong>{{ payloadStateLabel(currentPayloadState(line)) }}</strong>
                            </div>
                            <small>{{ payloadQuantitySummary(line) }}</small>
                        </div>
                        @if ((line.rejectedQuantity ?? 0) > 0 || (line.rescheduledQuantity ?? 0) > 0 || (line.notCollectedQuantity ?? 0) > 0) {
                        <div class="payload-status-strip"><i class="fa-solid fa-box-open"></i>
                            <div><span>Quantity outcome for {{ payloadLabelByLineId(line.shipmentLineId) }}</span>
                                <strong>Rejected {{ line.rejectedQuantity ?? 0 }} {{ payloadUnitByLineId(line.shipmentLineId) }} |
                                    Rescheduled {{ line.rescheduledQuantity ?? 0 }} {{ payloadUnitByLineId(line.shipmentLineId) }}
                                    @if (isDropoffLine(line) && (line.notCollectedQuantity ?? 0) > 0) { | Not collected {{ line.notCollectedQuantity }} }
                                </strong></div>
                        </div>
                        }

                        @if (availablePayloadStateOptions(line).length > 0) {
                        <div class="line-actions">
                            @for (state of availablePayloadStateOptions(line); track state) {
                            <button pButton type="button" [label]="payloadStateLabel(state)" [icon]="payloadStateIcon(state)" [outlined]="true"
                                [severity]="state === 'EXCEPTION' || state === 'SHORT' || state === 'DAMAGED' ? 'danger' : 'success'"
                                [disabled]="isPayloadLineTransitioning(line)" (click)="transitionStopLine(line, state)"></button>
                            }
                            @if (canRejectLine(line)) {
                            <button pButton type="button" label="Reject line" icon="fa-solid fa-rotate-left"
                                severity="warn" [outlined]="true" (click)="openRejectionDialog(line)"></button>
                            <button pButton type="button" label="Reschedule line" icon="fa-solid fa-calendar-days"
                                severity="warn" [outlined]="true" (click)="openRejectionDialog(line, 'RESCHEDULE')"></button>
                            }
                        </div>
                        } @else {
                        <div class="payload-terminal-note">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>Final payload status recorded.</span>
                            @if (canRejectLine(line)) {
                            <button pButton type="button" label="Reject line" icon="fa-solid fa-rotate-left"
                                severity="warn" [outlined]="true" (click)="openRejectionDialog(line)"></button>
                            <button pButton type="button" label="Reschedule line" icon="fa-solid fa-calendar-days"
                                severity="warn" [outlined]="true" (click)="openRejectionDialog(line, 'RESCHEDULE')"></button>
                            }
                        </div>
                        }

                        <div class="line-evidence-panel">
                            <div class="line-evidence-header">
                                <div>
                                    <span>Payload Evidence</span>
                                    <strong>{{ payloadEvidenceForLine(line).length }} file(s)</strong>
                                </div>
                                <button pButton type="button" [label]="payloadEvidenceActionLabel(line)" [icon]="payloadEvidenceActionIcon(line)"
                                    *ngIf="!isCancelled()"
                                    severity="secondary" [outlined]="true" (click)="openPayloadEvidenceUpload(line)"></button>
                            </div>
                            @if (isLoadingPayloadEvidence) {
                            <p-progressBar mode="indeterminate"></p-progressBar>
                            } @else if (payloadEvidenceForLine(line).length > 0) {
                            <div class="payload-evidence-list">
                                @for (evidence of payloadEvidenceForLine(line); track evidence.evidenceId || evidence.evidenceUrl) {
                                <div class="payload-evidence-row">
                                    @if (evidence.evidenceUrl) {
                                        <app-attachment-preview [url]="evidence.evidenceUrl" [name]="payloadEvidenceFileName(evidence)"></app-attachment-preview>
                                    } @else {
                                        <i [class]="payloadEvidenceIcon(evidence)"></i>
                                    }
                                    <div>
                                        @if (evidence.evidenceUrl) {
                                            <a [href]="evidence.evidenceUrl" target="_blank" rel="noopener"><strong>{{ payloadEvidenceFileName(evidence) }}</strong></a>
                                        } @else {
                                            <strong>{{ payloadEvidenceFileName(evidence) }}</strong>
                                        }
                                        <span>{{ displayState(evidence.evidenceType) }} | {{ formatDate(evidence.createdAt) }}</span>
                                    </div>
                                </div>
                                }
                            </div>
                            } @else {
                            <div class="payload-evidence-empty">
                                <i class="fa-solid fa-folder-open"></i>
                                <span>No payload evidence uploaded for this line yet.</span>
                            </div>
                            }
                        </div>
                    </article>
                    }
                </div>
                } @else {
                <div class="inline-empty">No payload operations are assigned to this site yet.</div>
                }
            </app-page-data-panel>

            <app-page-data-panel title="Payload Balance" subtitle="Planned and actual pickup/dropoff balance across the full delivery route.">
                @if (payloadBalances().length > 0) {
                <p-table [value]="payloadBalances()" responsiveLayout="scroll" styleClass="site-table">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Payload</th>
                            <th>Planned Pickup</th>
                            <th>Planned Dropoff</th>
                            <th>Planned Balance</th>
                            <th>Actual Pickup</th>
                            <th>Actual Dropoff</th>
                            <th>Actual Balance</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td><strong>{{ row.label }}</strong></td>
                            <td>{{ row.plannedPickup }}</td>
                            <td>{{ row.plannedDropoff }}</td>
                            <td><p-tag [value]="row.plannedBalance.toString()" [severity]="row.plannedBalance === 0 ? 'success' : 'warning'"></p-tag></td>
                            <td>{{ row.actualPickup }}</td>
                            <td>{{ row.actualDropoff }}</td>
                            <td><p-tag [value]="row.actualBalance.toString()" [severity]="row.actualBalance === 0 ? 'success' : 'warning'"></p-tag></td>
                        </tr>
                    </ng-template>
                </p-table>
                } @else {
                <div class="inline-empty">No route payload balance is available yet.</div>
                }
            </app-page-data-panel>
        </div>
    </div>

    <p-dialog [(visible)]="rejectionDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
        [style]="{ width: 'min(980px, 96vw)' }" [breakpoints]="{ '860px': '96vw' }"
        [header]="decisionAction === 'SKIP' ? 'Skip for revisit' : decisionAction === 'REJECT' ? 'Reject payload' : 'Reschedule payload'" styleClass="master-create-dialog site-decision-dialog">
        <div class="master-dialog-body">
            <aside class="master-dialog-summary">
                <div class="summary-icon"><i class="fa-solid fa-rotate-left"></i></div>
                <div><small>{{ decisionAction }} · {{ decisionTarget }}</small>
                    <h3>{{ decisionTarget === 'LINE' ? payloadLabelByLineId(rejectionLine?.shipmentLineId) : selectedStop?.locationName }}</h3>
                    <p>{{ decisionAction === 'SKIP' ? 'The untouched cargo will be handled on a required revisit.' :
                        defaultActionForStop() === 'DROPOFF' ? 'Remaining cargo will return on this delivery. The office confirms the destination.' :
                        'Cargo not collected at pickup creates no return.' }}</p></div>
                <div class="master-dialog-facts"><div><span>{{ decisionTarget === 'LINE' ? 'Remaining quantity' : 'Open lines' }}</span>
                    <strong>{{ decisionTarget === 'LINE' && rejectionLine ? remainingDecisionQuantity(rejectionLine) : siteOpenLines().length }}</strong></div>
                    <div><span>Evidence</span><strong>Optional</strong></div></div>
            </aside>
            <div class="master-dialog-form">
                @if (decisionError) { <div class="master-wide-field dialog-error" role="alert">{{ decisionError }}</div> }
                @if (decisionTarget === 'LINE' && decisionAction === 'REJECT') {
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-box-open"></i></p-inputGroupAddon>
                    <p-iftalabel><p-select inputId="decisionScope" [(ngModel)]="decisionScope"
                        [options]="['FULL', 'PARTIAL']" appendTo="body" styleClass="w-full"></p-select>
                        <label for="decisionScope">Rejection scope *</label></p-iftalabel></p-inputGroup>
                }
                @if (decisionTarget === 'LINE' && decisionScope === 'PARTIAL' && decisionAction === 'REJECT') {
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-rotate-left"></i></p-inputGroupAddon>
                    <p-iftalabel><p-inputNumber inputId="rejectionRejected" [(ngModel)]="rejectionRejectedQuantity"
                        [min]="0" [max]="rejectionLine ? remainingDecisionQuantity(rejectionLine) : 0" [maxFractionDigits]="3" styleClass="w-full"></p-inputNumber>
                        <label for="rejectionRejected">Rejected quantity *</label></p-iftalabel></p-inputGroup>
                }
                @if (decisionTarget === 'SITE' && decisionAction !== 'SKIP') {
                <div class="master-wide-field"><strong>Affected payload</strong>
                    @for (line of siteOpenLines(); track line.stopLineId) {
                    <div>{{ payloadLabelByLineId(line.shipmentLineId) }} — {{ remainingDecisionQuantity(line) }} {{ payloadUnitByLineId(line.shipmentLineId) }}</div>
                    }
                </div>
                }
                @if (needsPickupAllocation()) {
                <div class="master-wide-field"><strong>Allocate uncollected quantity to drop-offs</strong>
                    <small>Specify which planned drop-off quantities will not be carried.</small></div>
                @for (allocation of pickupAllocations; track allocation.stopLineId) {
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                    <p-iftalabel><p-inputNumber [inputId]="'allocation' + allocation.stopLineId"
                        [(ngModel)]="allocation.quantity" [min]="0" [max]="allocation.available"
                        [maxFractionDigits]="3" styleClass="w-full"></p-inputNumber>
                        <label [for]="'allocation' + allocation.stopLineId">{{ allocation.label }} (max {{ allocation.available }})</label>
                    </p-iftalabel></p-inputGroup>
                }
                @if (sitePickupAllocations.length > 0) {
                <div class="master-wide-field"><strong>Allocate uncollected pickup quantities</strong>
                    <small>Each item's allocation must equal its remaining quantity.</small></div>
                @for (allocation of sitePickupAllocations; track allocation.sourceLineId + '-' + allocation.stopLineId) {
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                    <p-iftalabel><p-inputNumber [inputId]="'siteAllocation' + allocation.sourceLineId + '-' + allocation.stopLineId"
                        [(ngModel)]="allocation.quantity" [min]="0" [max]="allocation.available"
                        [maxFractionDigits]="3" styleClass="w-full"></p-inputNumber>
                        <label [for]="'siteAllocation' + allocation.sourceLineId + '-' + allocation.stopLineId">
                            {{ allocation.sourceLabel }} → {{ allocation.label }} (max {{ allocation.available }})</label>
                    </p-iftalabel></p-inputGroup>
                }
                }
                }
                <p-inputGroup class="master-wide-field"><p-inputGroupAddon><i class="fa-solid fa-triangle-exclamation"></i></p-inputGroupAddon>
                    <p-iftalabel><p-select inputId="rejectionReason" [(ngModel)]="rejectionReasonCode"
                        [options]="rejectionReasons" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"
                        [loading]="reasonsLoading" [disabled]="reasonsLoading || !!reasonsError"></p-select>
                        <label for="rejectionReason">Reason *</label></p-iftalabel></p-inputGroup>
                @if (reasonsError) {
                <div class="master-wide-field dialog-error" role="alert">
                    <span>{{ reasonsError }}</span>
                    <p-button label="Retry" icon="pi pi-refresh" severity="secondary" [outlined]="true"
                        [disabled]="reasonsLoading" (onClick)="loadDecisionReasons()"></p-button>
                </div>
                }
                <p-inputGroup class="master-wide-field"><p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
                    <p-iftalabel><textarea id="rejectionRemarks" pTextarea rows="3" [(ngModel)]="rejectionRemarks"></textarea>
                        <label for="rejectionRemarks">Remarks</label></p-iftalabel></p-inputGroup>
                @if (decisionAction === 'SKIP') {
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-route"></i></p-inputGroupAddon>
                    <p-iftalabel><p-select inputId="afterStop" [(ngModel)]="selectedAfterStopId"
                        [options]="laterStopOptions()" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"
                        [placeholder]="selectedStop?.stopType === 'PICKUP' ? 'After current pickup' : 'After last customer stop'"></p-select><label for="afterStop">Revisit after</label></p-iftalabel></p-inputGroup>
                }
                @if (!rejectionQuantitiesValid()) { <small class="p-error master-wide-field">Choose a valid remaining quantity and action.</small> }
            </div>
        </div>
        <ng-template pTemplate="footer"><div class="master-dialog-footer">
            @if (permissionService.hasPermission(app_permissions.company.records.edit)) {
            <p-button label="Manage reasons" icon="fa-solid fa-list" severity="secondary" [text]="true"
                (onClick)="openReasonManager()"></p-button>
            }
            <span class="footer-spacer"></span>
            <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="rejectionDialogVisible = false"></p-button>
            <p-button [label]="decisionAction === 'SKIP' ? 'Create revisit' : decisionAction === 'REJECT' ? 'Confirm rejection' : 'Confirm reschedule'"
                icon="fa-solid fa-check" [loading]="rejectionSaving"
                [disabled]="!rejectionQuantitiesValid() || !rejectionReasonCode || reasonsLoading || !!reasonsError"
                (onClick)="submitRejection()"></p-button>
        </div></ng-template>
    </p-dialog>

    <p-dialog [(visible)]="reasonManagerVisible" [modal]="true" [draggable]="false" [resizable]="false"
        [style]="{ width: 'min(920px, 96vw)' }" [breakpoints]="{ '860px': '96vw' }"
        [header]="'Manage ' + decisionAction.toLowerCase() + ' reasons'"
        styleClass="master-create-dialog site-decision-dialog">
        <div class="master-dialog-body">
            <aside class="master-dialog-summary">
                <div class="summary-icon"><i class="fa-solid fa-list"></i></div>
                <div><small>Reason catalogue</small><h3>Company reason list</h3>
                    <p>These reasons are shared by portal and driver app users in this company.</p></div>
                <div class="managed-reason-list">
                @for (reason of managedReasons; track reason.reasonId) {
                <button pButton type="button" [label]="reason.label + (reason.active ? '' : ' (Inactive)')"
                    severity="secondary" [outlined]="true" (click)="editManagedReason(reason)"></button>
                }
                @if (!managedReasons.length && !reasonManagerLoading) {
                    <small>No company-specific reasons yet. Default reasons remain available.</small>
                }
                </div>
            </aside>
            <div class="master-dialog-form">
                @if (reasonManagerError) {
                <div class="master-wide-field dialog-error" role="alert"><span>{{ reasonManagerError }}</span>
                    <p-button label="Retry" icon="pi pi-refresh" severity="secondary" [outlined]="true"
                        [disabled]="reasonManagerLoading" (onClick)="loadManagedReasons()"></p-button></div>
                }
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                    <p-iftalabel><input pInputText id="reasonCode" [(ngModel)]="reasonForm.reasonCode" class="w-full" />
                        <label for="reasonCode">Reason code *</label></p-iftalabel></p-inputGroup>
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-tag"></i></p-inputGroupAddon>
                    <p-iftalabel><input pInputText id="reasonLabel" [(ngModel)]="reasonForm.label" class="w-full" />
                        <label for="reasonLabel">Label *</label></p-iftalabel></p-inputGroup>
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-toggle-on"></i></p-inputGroupAddon>
                    <p-iftalabel><p-select inputId="reasonActive" [(ngModel)]="reasonForm.active"
                        [options]="[{ label: 'Active', value: true }, { label: 'Inactive', value: false }]"
                        optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
                        <label for="reasonActive">Availability</label></p-iftalabel></p-inputGroup>
                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-sort-numeric-down"></i></p-inputGroupAddon>
                    <p-iftalabel><p-inputNumber inputId="reasonSort" [(ngModel)]="reasonForm.sortOrder"
                        [min]="0" styleClass="w-full"></p-inputNumber>
                        <label for="reasonSort">Sort order</label></p-iftalabel></p-inputGroup>
            </div>
        </div>
        <ng-template pTemplate="footer"><div class="master-dialog-footer">
            <p-button label="New reason" severity="secondary" [outlined]="true" (onClick)="resetManagedReason()"></p-button>
            <span class="footer-spacer"></span>
            <p-button label="Close" severity="secondary" [outlined]="true" (onClick)="reasonManagerVisible = false"></p-button>
            <p-button label="Save reason" icon="fa-solid fa-check" [loading]="reasonManagerSaving"
                [disabled]="reasonManagerLoading || !reasonForm.reasonCode.trim() || !reasonForm.label.trim()" (onClick)="saveManagedReason()"></p-button>
        </div></ng-template>
    </p-dialog>

    <p-dialog [(visible)]="overrideDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
        [style]="{ width: 'min(820px, 96vw)' }" [breakpoints]="{ '860px': '96vw' }"
        header="Override workflow gate" styleClass="master-create-dialog site-decision-dialog">
        <div class="master-dialog-body">
            <aside class="master-dialog-summary">
                <div class="summary-icon"><i class="fa-solid fa-shield-halved"></i></div>
                <div><small>Workflow control</small><h3>Gate requires review</h3>
                    <p>The requested site action was blocked. An authorized override needs a reason.</p></div>
                <div class="master-dialog-facts"><div><span>Blocked action</span>
                    <strong>{{ selectedStop?.locationName || 'Delivery site' }}</strong></div></div>
            </aside>
            <div class="master-dialog-form">
                <div class="master-wide-field dialog-error" role="alert">{{ overrideMessage }}</div>
                <p-inputGroup class="master-wide-field"><p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
                    <p-iftalabel><textarea id="overrideReason" pTextarea rows="4" maxlength="1000"
                        [(ngModel)]="overrideReason"></textarea>
                        <label for="overrideReason">Override reason *</label></p-iftalabel></p-inputGroup>
            </div>
        </div>
        <ng-template pTemplate="footer"><div class="master-dialog-footer">
            <span class="footer-spacer"></span>
            <p-button label="Cancel" severity="secondary" [outlined]="true"
                (onClick)="overrideDialogVisible = false"></p-button>
            <p-button label="Retry with override" icon="fa-solid fa-check"
                [disabled]="!overrideReason.trim()" (onClick)="confirmOverride()"></p-button>
        </div></ng-template>
    </p-dialog>
</app-page-shell>
